// frontend/src/pages/superadmin/SuperAdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  Building2, Plus, Edit2, Trash2, Search, X, CheckCircle, XCircle,
  Key, ChevronLeft, ChevronRight, Cpu
} from 'lucide-react';
import { getAirports, createAirport, updateAirport, deleteAirport, resetAirportAdminPassword, getAirportLicense, updateAirportAgentsLimit } from '../../services/superAdminApi';

const SuperAdminDashboard = () => {
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAirport, setEditingAirport] = useState(null);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [airportLicense, setAirportLicense] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updatingAgents, setUpdatingAgents] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    timezone: 'America/Chicago',
    max_staff: 50,
    max_owners: 500,
    max_agents: 5,
    expires_at: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadAirports();
  }, [page, searchTerm]);

  const loadAirports = async () => {
    setLoading(true);
    try {
      const response = await getAirports(page, 20, searchTerm);
      setAirports(response.data.airports || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error loading airports:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAirportLicense = async (airportId) => {
    try {
      const response = await getAirportLicense(airportId);
      setAirportLicense(response.data);
    } catch (error) {
      console.error('Error loading license:', error);
      setAirportLicense(null);
    }
  };

  const handleOpenAgentsModal = async (airport) => {
    setSelectedAirport(airport);
    await loadAirportLicense(airport.id);
    setShowAgentsModal(true);
  };

  const handleUpdateMaxAgents = async () => {
    if (!selectedAirport || !airportLicense) return;
    
    setUpdatingAgents(true);
    try {
      await updateAirportAgentsLimit(selectedAirport.id, { 
        max_agents: airportLicense.max_agents 
      });
      await loadAirports();
      await loadAirportLicense(selectedAirport.id);
      alert(`✅ Max agents updated to ${airportLicense.max_agents}`);
    } catch (error) {
      console.error('Error updating max agents:', error);
      alert('❌ Error updating max agents');
    } finally {
      setUpdatingAgents(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.subdomain) {
      alert('Name and subdomain are required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingAirport) {
        await updateAirport(editingAirport.id, formData);
      } else {
        await createAirport(formData);
      }
      setShowModal(false);
      resetForm();
      await loadAirports();
    } catch (error) {
      console.error('Error saving airport:', error);
      alert(error.response?.data?.detail || 'Error saving airport');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (airport) => {
    if (window.confirm(`Delete airport "${airport.name}"? This will delete ALL its data!`)) {
      try {
        await deleteAirport(airport.id);
        await loadAirports();
      } catch (error) {
        console.error('Error deleting airport:', error);
        alert('Error deleting airport');
      }
    }
  };

  const handleResetPassword = async (airport) => {
    try {
      const response = await resetAirportAdminPassword(airport.id);
      setResetResult({
        airport: airport.name,
        password: response.data.new_password
      });
      setTimeout(() => setResetResult(null), 10000);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subdomain: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      timezone: 'America/Chicago',
      max_staff: 50,
      max_owners: 500,
      max_agents: 5,
      expires_at: ''
    });
    setEditingAirport(null);
  };

  const handleEdit = (airport) => {
    setEditingAirport(airport);
    setFormData({
      name: airport.name || '',
      subdomain: airport.subdomain || '',
      contact_email: airport.contact_email || '',
      contact_phone: airport.contact_phone || '',
      address: airport.address || '',
      timezone: airport.timezone || 'America/Chicago',
      max_staff: airport.max_staff || 50,
      max_owners: airport.max_owners || 500,
      max_agents: airport.max_agents || 5,
      expires_at: airport.license_expires ? airport.license_expires.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Airports Management</h1>
          <p className="text-gray-400 text-sm">Manage client airports and their agent licenses</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Airport
        </button>
      </div>

      {/* Reset Password Result */}
      {resetResult && (
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
          <p className="text-green-400 font-medium">Password reset for {resetResult.airport}</p>
          <p className="text-white text-lg font-mono mt-1">{resetResult.password}</p>
          <p className="text-gray-400 text-xs mt-1">
            Give this password to the airport administrator.
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Search airports by name, subdomain, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white"
        />
      </div>

      {/* Airports List */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="p-4 text-left text-gray-400">Airport</th>
                <th className="p-4 text-left text-gray-400">Subdomain</th>
                <th className="p-4 text-left text-gray-400">Contact</th>
                <th className="p-4 text-left text-gray-400">Agents</th>
                <th className="p-4 text-left text-gray-400">License</th>
                <th className="p-4 text-left text-gray-400">Status</th>
                <th className="p-4 text-left text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-gray-400">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
                  </td>
                </tr>
              ) : airports.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No airports found
                  </td>
                </tr>
              ) : (
                airports.map((airport) => (
                  <tr key={airport.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">{airport.name}</p>
                        <p className="text-gray-500 text-xs">ID: {airport.id.substring(0, 8)}...</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <code className="text-blue-400 text-sm">{airport.subdomain}.aerotrick.com</code>
                    </td>
                    <td className="p-4">
                      <p className="text-gray-300 text-sm">{airport.contact_email || '-'}</p>
                      <p className="text-gray-500 text-xs">{airport.contact_phone || '-'}</p>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                        {airport.used_agents || 0}/{airport.max_agents || 5}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        <p className="text-xs">
                          <span className="text-gray-500">Staff:</span> {airport.max_staff || 50}
                        </p>
                        <p className="text-xs">
                          <span className="text-gray-500">Owners:</span> {airport.max_owners || 500}
                        </p>
                        <p className="text-xs">
                          <span className="text-gray-500">Expires:</span> {formatDate(airport.license_expires)}
                        </p>
                      </div>
                    </td>
                    <td className="p-4">
                      {airport.is_active ? (
                        <span className="flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircle className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-xs">
                          <XCircle className="w-3 h-3" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(airport)}
                          className="p-1.5 hover:bg-gray-700 rounded-lg text-blue-400"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenAgentsModal(airport)}
                          className="p-1.5 hover:bg-gray-700 rounded-lg text-purple-400"
                          title="Manage Agents"
                        >
                          <Cpu className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(airport)}
                          className="p-1.5 hover:bg-gray-700 rounded-lg text-yellow-400"
                          title="Reset Admin Password"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(airport)}
                          className="p-1.5 hover:bg-gray-700 rounded-lg text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-gray-700">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-gray-400 text-sm py-2">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Modal Création/Édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">
                {editingAirport ? 'Edit Airport' : 'New Airport'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Airport Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    placeholder="Boerne Stage Field"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Subdomain *</label>
                  <input
                    type="text"
                    value={formData.subdomain}
                    onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    placeholder="boerne"
                  />
                  <p className="text-xs text-gray-500 mt-1">.aerotrick.com</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows="2"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Timezone</label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">License Expiry</label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Staff</label>
                  <input
                    type="number"
                    value={formData.max_staff}
                    onChange={(e) => setFormData({ ...formData, max_staff: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Owners</label>
                  <input
                    type="number"
                    value={formData.max_owners}
                    onChange={(e) => setFormData({ ...formData, max_owners: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Agents</label>
                  <input
                    type="number"
                    value={formData.max_agents}
                    onChange={(e) => setFormData({ ...formData, max_agents: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium disabled:opacity-50"
              >
                {submitting ? 'Saving...' : (editingAirport ? 'Update' : 'Create')}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Agents Management */}
      {showAgentsModal && selectedAirport && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div>
                <h3 className="text-white font-semibold">
                  Agents for {selectedAirport.name}
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  Manage video surveillance agents
                </p>
              </div>
              <button onClick={() => setShowAgentsModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* License Information */}
              <div className="bg-gray-900 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">License Key:</span>
                  <code className="text-blue-400 text-sm bg-gray-800 px-2 py-1 rounded break-all max-w-[60%]">
                    {airportLicense?.license_key}
                  </code>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Agents Used:</span>
                  <span className="text-white font-medium">
                    {airportLicense?.used_agents || 0} / {airportLicense?.max_agents || 5}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Max Agents:</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={airportLicense?.max_agents || 5}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value);
                        setAirportLicense(prev => ({ ...prev, max_agents: newValue }));
                      }}
                      className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm"
                    />
                    <button
                      onClick={handleUpdateMaxAgents}
                      disabled={updatingAgents}
                      className="px-3 py-1 bg-purple-500 hover:bg-purple-600 rounded text-white text-sm disabled:opacity-50 flex items-center gap-1"
                    >
                      {updatingAgents ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        'Update'
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Expires:</span>
                  <span className="text-white">
                    {airportLicense?.expires_at ? new Date(airportLicense.expires_at).toLocaleDateString() : 'Never'}
                  </span>
                </div>
              </div>

              {/* Installation Instructions */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-sm font-medium mb-2">📋 Installation Instructions</p>
                <p className="text-gray-300 text-sm">Give this license key to the client for agent installation:</p>
                <code className="block bg-gray-900 p-2 rounded mt-2 text-blue-400 text-sm break-all">
                  {airportLicense?.license_key}
                </code>
                <p className="text-gray-400 text-xs mt-2">
                  The agent will automatically link to this airport when installed with this key.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={() => setShowAgentsModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;